/**
 * Import Helper Functions
 * Utility functions for building import requests and data transformation.
 * Extracted from actions.js to follow Single Responsibility Principle.
 */

/**
 * Convert camelCase string to PascalCase.
 * Required because API expects system field keys in PascalCase (e.g., "Name", "Plant").
 * @param {string} str - camelCase string (e.g., "name", "funcLocation")
 * @returns {string} PascalCase string (e.g., "Name", "FuncLocation")
 */
export function toPascalCase(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Build the import request object from wizard state.
 * @param {Object} state - Vuex state object containing wizard configuration
 * @returns {Object} Import request payload for the API
 */
export function buildImportRequest(state) {
  const schema = buildSchema(state);

  // Get the Excel column mapped to 'name' (required field)
  const nameExcelColumn = state.columnMappings['name'];

  // Filter to rows with valid 'name' values (required field)
  const validRows = state.parsedData.filter(row => {
    const nameValue = row[nameExcelColumn];
    return nameValue !== undefined && nameValue !== null && String(nameValue).trim() !== '';
  });

  const records = validRows.map(row => {
    const systemFields = {};
    const customFields = {};

    // Map system fields - convert keys to PascalCase for API compatibility
    // API expects keys like "Name", "Plant", "FuncLocation" (see MasterImportService.cs)
    Object.entries(state.columnMappings).forEach(([sysField, excelCol]) => {
      if (excelCol && row[excelCol] !== undefined && row[excelCol] !== null && row[excelCol] !== '') {
        systemFields[toPascalCase(sysField)] = row[excelCol];
      }
    });

    // Map custom fields (only selected ones)
    schema.forEach(def => {
      const value = row[def.originalName];
      if (value !== undefined && value !== null && value !== '') {
        customFields[def.name] = convertValue(value, def.dataType);
      }
    });

    return { systemFields, customFields };
  });

  return {
    targetType: state.selectedScopeType,
    targetId: state.selectedTargetId,
    tagType: state.selectedTagType,
    batchName: state.batchName || generateBatchName(state),
    sourceFileName: state.sourceFileName,
    duplicateHandling: state.duplicateHandling,
    schema,
    records,
  };
}

/**
 * Build schema definition from custom attribute selections.
 * @param {Object} state - Vuex state object
 * @returns {Array<Object>} Schema definitions for custom attributes
 */
export function buildSchema(state) {
  return Object.entries(state.customAttributeSelections)
    .filter(([col, selected]) => {
      // Skip empty column names to prevent backend validation errors
      // (RegisterColumnDefinition.Name is required)
      if (!selected) return false;
      const normalized = normalizeKey(col);
      return normalized !== '';
    })
    .map(([col], index) => ({
      name: normalizeKey(col),
      displayName: state.customDisplayNames[col] || col,
      originalName: col,
      dataType: state.customDataTypes[col] || 'text',
      order: index + 1,
      isSystemColumn: false,
      isMandatory: false,
    }));
}

/**
 * Normalize column name to camelCase key.
 * Converts "Operating Pressure" to "operatingPressure".
 * @param {string} columnName - Original column name from Excel
 * @returns {string} Normalized camelCase key
 * @example
 * normalizeKey("Operating Pressure") // "operatingPressure"
 * normalizeKey("Serial Number") // "serialNumber"
 */
export function normalizeKey(columnName) {
  return columnName
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(' ')
    .filter(Boolean)
    .map((word, i) =>
      i === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');
}

/**
 * Convert value based on data type.
 * @param {*} value - Raw value from Excel
 * @param {string} dataType - Target data type ('text' | 'number' | 'boolean' | 'date')
 * @returns {*} Converted value
 */
export function convertValue(value, dataType) {
  if (value === null || value === undefined) return null;

  switch (dataType) {
    case 'number':
      return parseFloat(value) || 0;
    case 'boolean':
      return ['true', '1', 'yes', 'y'].includes(String(value).toLowerCase());
    case 'date':
      return value; // Keep as string, backend handles conversion
    default:
      return String(value);
  }
}

/**
 * Generate default batch name from state.
 * @param {Object} state - Vuex state object
 * @returns {string} Generated batch name
 */
export function generateBatchName(state) {
  const date = new Date().toISOString().split('T')[0];
  const baseName = state.sourceFileName
    ? state.sourceFileName.replace(/\.[^/.]+$/, '')
    : 'Import';
  return `${state.selectedTagType || ''} ${baseName} ${date}`.trim();
}

/**
 * Validate that all required fields are mapped.
 * @param {Object} columnMappings - Current column mappings
 * @param {Array} requiredFields - Array of required field keys
 * @returns {Object} Validation result with isValid and missingFields
 */
export function validateRequiredMappings(columnMappings, requiredFields = ['name']) {
  const missingFields = requiredFields.filter(field => !columnMappings[field]);
  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Calculate mapping statistics from current state.
 * @param {Object} columnMappings - System field to Excel column mappings
 * @param {Object} customAttributeSelections - Custom attribute selection state
 * @param {Array} systemFields - Array of available system fields
 * @returns {Object} Mapping statistics
 */
export function calculateMappingStats(columnMappings, customAttributeSelections, systemFields) {
  const mappedSystemCount = Object.values(columnMappings).filter(Boolean).length;
  const selectedCustomCount = Object.values(customAttributeSelections).filter(Boolean).length;
  const totalSystemFields = systemFields?.length || 0;

  return {
    mappedSystemCount,
    selectedCustomCount,
    totalSystemFields,
    mappingProgress: totalSystemFields > 0
      ? Math.round((mappedSystemCount / totalSystemFields) * 100)
      : 0,
  };
}

export default {
  buildImportRequest,
  buildSchema,
  normalizeKey,
  toPascalCase,
  convertValue,
  generateBatchName,
  validateRequiredMappings,
  calculateMappingStats,
};
